import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { GhostButton, TransparentFlatButton } from '@ui/buttons';
import { createTimeInstance } from '@utils/time';
import { formatCount, formatFeedId } from './format';
import { summarizeScope } from './querySummary';
import { runKey } from './runHistory';
import { useThemeToken } from './useThemeToken';

const List = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  overflow: hidden;
`;

// A row is two controls side by side rather than one button holding another: interactive content
// cannot nest, so a remove button inside the re-run button would be invalid markup and would not
// reliably render or receive clicks.
const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }
`;

// Inset ring (-2): these sit flush inside a bordered row, and an outside ring would overlap the
// row's own border and its neighbour's.
const Item = styled(GhostButton)`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  text-align: left;
  padding: 10px 12px;

  /* Dimmed rather than hidden or removed: the row the read is for is the one thing on screen that
     says WHICH run is running, so it has to stay legible while it says it. Still focusable — the
     press that started it left focus here, and a native disabled attribute would drop it to body. */
  ${({ $busy }) => ($busy ? 'opacity: 0.65; cursor: default;' : '')}

  &:focus-visible {
    outline-offset: -2px;
  }
`;

const Remove = styled(GhostButton)`
  flex: 0 0 auto;
  padding: 0 12px;
  font-size: 15px;
  line-height: 1;
  color: ${({ theme }) => theme.themeColors.secondaryText};

  &:hover {
    color: ${({ theme }) => theme.themeColors.text};
  }

  &:focus-visible {
    outline-offset: -2px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

// Shaped like the disclosure in RowPayload: a plain text button, not a third button style competing
// with Clear in the header.
//
// The CTA colour is passed to the label rather than set here, because it would not survive: BaseText
// writes `color` onto its own span (and onto `*` beneath it), so a colour inherited from this button
// is overridden by the text component inside it.
//
// Vertical padding rather than none: the label is 14px tall, which is under the 24px minimum target
// size, and this is a control in a list of much larger ones.
const Toggle = styled(GhostButton)`
  padding: 5px 0;
`;

// The list sits above the form, so its full ten rows push the thing the page is actually for off
// the screen. Three is enough to recognise "the read I just did" without costing the fold; the
// rest are one press away rather than dropped.
export const VISIBLE_RUN_LIMIT = 3;

const LIST_ID = 'feed-view-recent-runs';

// Says what was asked for, not what came back — a run is recognised by its window and companies.
export const describeRunScope = (run) =>
  summarizeScope({ from: run.knowableFrom, to: run.knowableTo, companies: run.companies, slim: run.slim });

// Just the count. Runs recorded before the truncation model was deleted still carry an `atCap` flag
// in localStorage; it is deliberately not read, because it was never true of the server — a read
// over the cap is refused with a 422 and never becomes a listed run at all. Old records therefore
// need no migration: the stale key is simply ignored and the run reads as the complete one it was.
export const describeRunResult = (run) => {
  if (run.rowCount === null || run.rowCount === undefined) return null;
  return `${formatCount(run.rowCount)} row${run.rowCount === 1 ? '' : 's'}`;
};

// The last handful of reads, so a query worth repeating doesn't have to be rebuilt. Pressing one
// restores its window and company filter and runs it again.
//
// Only the freshest few show by default; the toggle names how many are hidden, so the list says
// there is more history behind it rather than appearing to be all there ever was.
//
// `runningKey` is the identity of the query a read is currently for, or null — matched with the
// same `runKey` the history dedupes on, so a row is marked because it IS that query rather than
// because it happens to sit at that index. A read takes ~24 seconds, and without this the only
// acknowledgement a press got was a status line further down the page.
export const RecentRuns = ({ runs, onSelect, onRemove, onClear, runningKey = null }) => {
  // Declared above the guard because the guard is a conditional return, and a hook after one runs
  // on some renders and not others.
  const [isExpanded, setIsExpanded] = React.useState(false);
  const themeToken = useThemeToken();

  const hiddenCount = Math.max((runs?.length || 0) - VISIBLE_RUN_LIMIT, 0);

  // An empty list renders null but stays mounted, so without this an expansion outlives the history
  // it was opened over: Clear, then a few new reads, and the list comes back untruncated with no one
  // having asked for it. Dropping the state whenever nothing is hidden makes expansion a statement
  // about the list in front of the reader rather than a setting for the session.
  React.useEffect(() => {
    if (hiddenCount === 0) setIsExpanded(false);
  }, [hiddenCount]);

  if (!runs || runs.length === 0) return null;

  const visibleRuns = isExpanded ? runs : runs.slice(0, VISIBLE_RUN_LIMIT);

  return (
    <Container bottom={16}>
      <Header>
        <Body5 bold>Recent runs</Body5>
        <TransparentFlatButton onClick={onClear}>Clear</TransparentFlatButton>
      </Header>
      <Container top={8}>
        <List id={LIST_ID}>
          {visibleRuns.map((run, index) => {
            const scope = describeRunScope(run);
            const outcome = describeRunResult(run);
            const isBusy = !!runningKey && runKey(run) === runningKey;
            return (
              <Row key={`${run.feedId}-${run.ranAt || index}`}>
                <Item
                  type="button"
                  $busy={isBusy}
                  aria-busy={isBusy || undefined}
                  // Pressing the run that is already running is the one press with nothing to do:
                  // it would abort the read in flight only to re-issue the same one. Every OTHER
                  // row stays live, because superseding is a supported path — useFeedView aborts
                  // the previous request and guards on a monotonic id — and a misclick should be
                  // correctable by pressing the right run, not by cancelling first.
                  aria-disabled={isBusy || undefined}
                  onClick={() => {
                    if (isBusy) return;
                    onSelect(run);
                  }}
                  aria-label={
                    isBusy
                      ? `Running ${formatFeedId(run.feedId)} — ${scope}`
                      : `Run ${formatFeedId(run.feedId)} again — ${scope}`
                  }
                >
                  <Body5>{formatFeedId(run.feedId)}</Body5>
                  {/* While it runs, the row drops what the LAST read of this query returned. The
                      count and the timestamp are true of that earlier read, and leaving them beside
                      a live one reads as the running read's own answer, arriving impossibly early. */}
                  <Body7 isLowContrast>
                    {scope}
                    {isBusy ? ' · Running…' : outcome ? ` · ${outcome}` : ''}
                    {!isBusy && run.ranAt ? ` · ${createTimeInstance(run.ranAt).fromNow()}` : ''}
                  </Body7>
                </Item>
                {onRemove && (
                  <Remove
                    type="button"
                    onClick={() => onRemove(run)}
                    aria-label={`Forget ${formatFeedId(run.feedId)} — ${scope}`}
                    title="Forget this run"
                  >
                    <span aria-hidden="true">×</span>
                  </Remove>
                )}
              </Row>
            );
          })}
        </List>
        {hiddenCount > 0 && (
          <Container top={8}>
            {/* Below the list rather than beside the heading: expanded, "Show less" is where the
                reader's eye ends up, and collapsed the count reads as a continuation of the rows
                above it. */}
            <Toggle
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-expanded={isExpanded}
              aria-controls={LIST_ID}
            >
              <Body7 bold color={themeToken('primaryCtaButton')}>
                {isExpanded ? 'Show less' : `Show ${formatCount(hiddenCount)} more`}
              </Body7>
            </Toggle>
          </Container>
        )}
      </Container>
    </Container>
  );
};
